/**
 * CoBrandLockup — ONE source of truth for the co-branded identity shown in the
 * top nav (`variant="nav"`) and on the dashboard PortfolioCard (`variant="card"`).
 *
 * Previously each surface hand-composed the lockup independently and had drifted:
 * the nav put Datacrate first, the card put the customer first, the customer logo
 * was 28px one place and 36px the other, and both aligned by bounding-box rather
 * than cap-height. Centralising the composition here keeps the two in lockstep.
 *
 * Treatment "hero + micro-footer" (customer-first / white-label):
 *  - With a CUSTOM org logo, the CUSTOMER mark is the hero (dominant, centred)
 *    and Datacrate is demoted to a quiet "POWERED BY · DATACRATE" endorsement
 *    micro-line beneath it. The endorsement reuses <BrandLockup> (tagline
 *    "Powered by") so it stays pixel-exact to the brand and theme-aware — and so
 *    its sub-10px tagline lives in exempt SVG <text> rather than a className the
 *    design-tokens sentinel hard-forbids (`text-[8px]`). A low opacity keeps it a
 *    tasteful footnote that never competes with the customer mark (~2:1 optical
 *    dominance).
 *  - With NO custom logo (the platform org itself), there is no customer to lead,
 *    so the Datacrate lockup renders alone — exactly as before.
 */
import React from 'react';
import { BrandLockup } from './BrandLockup';

interface Props {
  /** Customer logo src (already a usable URL/relative path). Falsy → Datacrate alone. */
  customerLogoUrl?: string | null;
  /** Accessible name for the customer logo (org/company name). */
  customerName?: string | null;
  /** nav = compact top-bar strip · card = roomier dashboard card. */
  variant?: 'nav' | 'card';
  className?: string;
}

export function CoBrandLockup({
  customerLogoUrl,
  customerName,
  variant = 'nav',
  className,
}: Props): React.JSX.Element {
  // Solo Datacrate — the platform org (or any org without an uploaded mark).
  if (!customerLogoUrl) {
    return (
      <div className={`flex items-center justify-center ${className ?? ''}`}>
        <BrandLockup
          height={variant === 'card' ? 46 : 38}
          tagline="Site Intelligence"
          title="Datacrate · Site Intelligence"
        />
      </div>
    );
  }

  // Customer-first hero + quiet "powered by Datacrate" endorsement beneath. The
  // endorsement heights are tuned (rendered, not computed) so the DATACRATE
  // wordmark stays legible — BrandLockup's wordmark is only ~0.37× its box
  // height, so a 13px lockup yields a ~5px wordmark (illegible). 16/20px keep it
  // readable while the denser customer mark still reads as the hero. NAV PADDING:
  // the stack is sized to leave even breathing room in the 56px header rail —
  // 24 + 4px gap + 16 = 44px → ~6px top/bottom padding (was 50px/3px = cramped).
  const heroHeight = variant === 'card' ? 36 : 24;
  const endorseHeight = variant === 'card' ? 20 : 16;

  return (
    <div
      className={`flex flex-col items-center ${variant === 'card' ? 'gap-2' : 'gap-1'} min-w-0 ${className ?? ''}`}
    >
      {/* HERO — the customer owns the identity. Bounded (fixed height + max-width
          + object-contain) so an arbitrary uploaded mark can't break the layout.
          The wrapper's data attribute drives the white-on-dark filter (index.css)
          so dark customer marks stay visible on the dark rail/card. */}
      <div className="flex items-center justify-center shrink-0" data-logo-white-on-dark="true">
        <img
          src={customerLogoUrl}
          alt={customerName ? `${customerName} logo` : 'Organization logo'}
          className="w-auto max-w-[8rem] object-contain"
          style={{ height: heroHeight }}
          fetchPriority={variant === 'card' ? 'high' : 'auto'}
          decoding="async"
        />
      </div>
      {/* ENDORSEMENT — Datacrate demoted to a quiet "POWERED BY · DATACRATE"
          micro-line; opacity keeps it a footnote, never a co-equal brand. */}
      <BrandLockup
        height={endorseHeight}
        tagline="Powered by"
        title="Powered by Datacrate"
        className="opacity-75"
      />
    </div>
  );
}

export default CoBrandLockup;
